/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import fetch from 'cross-fetch';
import config from '../config'

export default class EinsteinAPI {
    constructor(siteId) {
        this.einsteinConfig = config.einstein.find(s => s.siteId === siteId);
    }

    /**
     * Given a POJO append the correct user and cookie identifier values using the current auth state.
     *
     * @param {object} params
     * @returns {object} The decorated body object.
     */
    _buildBody(params) {
        const instanceType_prd = 'prd';
        const instanceType_sbx = 'sbx';

        const body = {...params};

        // The first part of the siteId is the realm
        if (this.einsteinConfig.siteId) {
            body.realm = this.einsteinConfig.siteId.split('-')[0];
        }

        if (this.einsteinConfig.isProduction) {
            body.instanceType = instanceType_prd;
        } else {
            body.instanceType = instanceType_sbx;
        }

        return body;
    }

    /**
     * Given a product or item source, returns the product data that Einstein requires
     */
    _constructEinsteinProduct(product) {
        if (product.type && (product.type.master || product.type.variant)) {
            // handle variants for PDP / viewProduct
            // Assumes product is a Product object from SCAPI Shopper-Products:
            // https://developer.salesforce.com/docs/commerce/commerce-api/references/shopper-products?meta=type%3AProduct
            return {
                id: product.master.masterId,
                sku: product.id,
                altId: '',
                altIdType: ''
            }
        } else if (
            product.productType &&
            (product.productType.master || product.productType.variant)
        ) {
            // handle variants for PLP / viewCategory & viewSearch
            // Assumes product is a ProductSearchHit from SCAPI Shopper-Search:
            // https://developer.salesforce.com/docs/commerce/commerce-api/references/shopper-search?meta=type%3AProductSearchHit
            return {
                id: product.productId,
                sku: product.productId, //TODO: Should we switch this to product.representedProduct.id once we allow non-master products in search results?
                altId: '',
                altIdType: ''
            };
        } else {
            // handles non-variants
            return {
                id: product.id,
                sku: '',
                altId: '',
                altIdType: ''
            };
        }
    }

    async einsteinFetch(endpoint, method, body) {
        const config = this.einsteinConfig;
        const {host, einsteinId} = config;

        const headers = {
            'Content-Type': 'application/json',
            'x-cq-client-id': einsteinId
        };

        // Include `userId` and `cookieId` parameters.
        if (body) {
            body = this._buildBody(body);
        }

        let response = await fetch(`${host}/v3${endpoint}`, {
            method: method,
            headers: headers,
            ...(body && {
                body: JSON.stringify(body)
            })
        });

        return await response.json();
    }

    /**
     * Get a list of recommenders that can be used in recommendation requests.
     * https://developer.salesforce.com/docs/commerce/einstein-api/references#einstein-recommendations:Summary
     **/
    async getRecommenders() {
        const endpoint = `/personalization/recommenders/${this.einsteinConfig.siteId}`;
        const method = 'GET';
        const body = null;

        return this.einsteinFetch(endpoint, method, body);
    }

    /**
     * Get a set of recommendations
     * https://developer.salesforce.com/docs/commerce/einstein-api/references#einstein-recommendations:Summary
     **/
    async getRecommendations(products, args) {
        const endpoint = `/personalization/recs/${this.einsteinConfig.siteId}/${this.einsteinConfig.recommenderName}`;
        const method = 'POST';
        const body = {
            products: products?.map((product) => this._constructEinsteinProduct(product)),
            ...args
        };

        // Fetch the recommendations
        const reco = await this.einsteinFetch(endpoint, method, body);

        reco.recommenderName = this.einsteinConfig.recommenderName;

        return reco;
    }

    /**
     * Get a set of recommendations for a zone
     * https://developer.salesforce.com/docs/commerce/einstein-api/references#einstein-recommendations:Summary
     **/
    async getZoneRecommendations(zoneName, products, args) {
        const endpoint = `/personalization/${this.einsteinConfig.siteId}/zones/${zoneName}/recs`;
        const method = 'POST';

        const body = {
            products: products?.map((product) => this._constructEinsteinProduct(product)),
            ...args
        };

        // Fetch the recommendations
        const reco = await this.einsteinFetch(endpoint, method, body);

        return this.fetchRecProductDetails(reco);
    }

}
